import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Grid,
  Image,
  Text,
  useBreakpointValue,
} from "@chakra-ui/react";
import CustomApprovalModal from "@/shared/components/custom-modal";
import AppButton from "@/features/line-sheet-sets/app-button";
import AppCheckBox from "@/features/line-sheet-sets/app-checkbox";
import SearchInput from "@/shared/components/search-input";
import CustomIcon from "../../../../../public/icons-v1_1";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useMembership from "@/shared/hooks/useMembership";
import PathPickBadge from "@/features/pathpick/components/ui/PathPickBadge";
import PathPickPagination from "@/features/pathpick/components/ui/PathPickPagination";
import PathPickCopyableSkuText from "@/features/pathpick/components/product/PathPickCopyableSkuText";
import PathPickRequestStatusChip from "@/features/pathpick/components/request/PathPickRequestStatusChip";
import PathPickRequestFilterTabs from "@/features/pathpick/components/request/PathPickRequestFilterTabs";
import PathPickRequestFilterModal from "@/features/pathpick/components/request/PathPickRequestFilterModal";
import PathPickManageProductModal from "@/features/pathpick/components/request/PathPickManageProductModal";
import PathPickUnsyncConfirmModal from "@/features/pathpick/components/request/PathPickUnsyncConfirmModal";
import PathPickRetryConfirmModal from "@/features/pathpick/components/request/PathPickRetryConfirmModal";
import PathPickRequestFileDownloadButton from "@/features/pathpick/components/request/PathPickRequestFileDownloadButton";
import CenteredSpinner from "@/shared/components/centered-spinner";
import CustomNoResults from "@/shared/components/no-results/CustomNoResults";
import useGetPathPickRequestDetail from "@/features/pathpick/hooks/query/useGetPathPickRequestDetail";
import usePathPickUnsyncProducts from "@/features/pathpick/hooks/usePathPickUnsyncProducts";
import usePathPickRetryRequests, {
  PathPickRetryTarget,
} from "@/features/pathpick/hooks/usePathPickRetryRequests";
import {
  formatRequestDateTime,
  getPathPickRequestItemStatusMeta,
  getPathPickSyncStatusMeta,
  getTotalOptionQuantity,
  isFailedStatusMeta,
  isRequestDetailItemSyncActive,
  getFailureNoteKey,
  isRetryableErrorType,
} from "@/features/pathpick/utils/request";
import { PathPickUnsyncTarget } from "@/features/pathpick/types";
import {
  PathPickMutationProduct,
  PathPickProductKey,
  PathPickProductRequestKind,
  PathPickRequestFormat,
  PathPickStoreName,
} from "@/features/pathpick/types";
import { toPathPickProductKey } from "@/features/pathpick/utils";
import {
  IS_REQUEST_RETRY_ENABLED,
  PathPickRequestStatusTab,
  REQUEST_STATUS_TABS,
  REQUEST_STATUS_TAB_LABEL_KEY,
  toStatusFilterParam,
} from "@/features/pathpick/constants";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  // 볼 요청의 id. 요청 헤더(구분·신청일시·건수)까지 /requests/{id} 응답에서 읽으므로
  // 호출부가 요청 행 전체를 들고 있지 않아도 된다 — 상품 카드는 슬롯의 requestId만 안다.
  requestId: number;
  // 검색창 초기값. 상품 카드의 "확인 필요" 배지로 열 때 그 상품의 SKU를 넣어
  // 해당 상품만 걸러진 상태로 보여준다.
  initialQuery?: string;
  // 검색어가 커밋될 때(엔터/검색 버튼) 호출된다. 등록 신청 내역이 검색어를 URL에 실어
  // 링크로 공유할 수 있게 하려고 둔 훅 — 넘기지 않으면 모달 안에서만 검색이 끝난다.
  onQueryCommit?: (query: string) => void;
}

const DEFAULT_PAGE_SIZE = 20;

// 선택 열 — 일괄 처리(연동 해제)가 걸리는 요청에만 붙는다.
const SELECT_COLUMN = "32px";
// 연동 요청 — 상품 / 현재 재고 / 연동일 / 최근 업데이트 / 상태 / 비고 / 관리
const TEMPLATE_COLUMNS =
  "minmax(280px, 1.6fr) 100px 140px 140px 140px minmax(120px, 1fr) 120px";
// 다운로드 요청 — 연동 전용 컬럼(최근 업데이트)만 뺀다.
const DOWNLOAD_TEMPLATE_COLUMNS =
  "minmax(280px, 1.6fr) 100px 140px 140px minmax(150px, 1fr) 120px";

const SYNC_COLUMN_KEYS = [
  "column_product",
  "column_stock",
  "column_synced_at",
  "column_refreshed_at",
  "column_status",
  "column_note",
  "column_manage",
] as const;
const DOWNLOAD_COLUMN_KEYS = [
  "column_product",
  "column_stock",
  "column_requested_at",
  "column_status",
  "column_note",
  "column_manage",
] as const;

// 선택 항목 — 페이지를 넘겨도 선택이 유지되므로 일괄 버튼 노출·카운트 판정에 필요한
// 능력치(연동 해제 가능 / 재생성 가능)까지 함께 들고 있는다.
interface SelectedItem extends PathPickMutationProduct {
  canUnsync: boolean;
  canRetry: boolean;
}

// 연동 요청의 format은 채널 그 자체라 연동 해제의 store_names 값과 같다.
// 파일 양식(KREAM 등)이나 format이 없는 요청은 해제할 스토어를 정할 수 없어 null이다.
const toPathPickStoreName = (
  format: PathPickRequestFormat | null | undefined
): PathPickStoreName | null => {
  switch (format) {
    case "NAVER":
    case "MUSTIT":
    case "TRENBE":
    case "CAFE24":
      return format;
    default:
      return null;
  }
};

function PathPickRequestProductsModal({
  isOpen,
  onClose,
  requestId,
  initialQuery,
  onQueryCommit,
}: Props) {
  const { t } = useI18nHelper();

  // 체험·멤버십 만료 시 재생성·연동 해제 같은 쓰기 동작만 잠근다(조회·검색은 그대로).
  const { isMembershipExpired } = useMembership();
  const lockedProps = isMembershipExpired
    ? {
        isDisabled: true,
        disabledMessage: t("path_pick:membership_lock.disabled_tooltip"),
      }
    : undefined;

  // 입력값(search)과 서버로 보내는 커밋값(query)을 분리해 타이핑마다 요청이 나가지 않게 한다.
  const [search, setSearch] = useState<string>(initialQuery ?? "");
  const [query, setQuery] = useState<string>(initialQuery ?? "");
  // 처리 상태 탭 — 서버가 status__in으로 걸러 주므로 totalCount/페이지네이션이 함께 따라온다.
  const [statusTab, setStatusTab] = useState<PathPickRequestStatusTab>("ALL");
  // 요청 아이템 id로 선택 상태를 잡고, 값으로는 요청 body에 넣을 상품 참조(productId + 유형)를 들고 있는다.
  const [selectedProducts, setSelectedProducts] = useState<
    Map<number, SelectedItem>
  >(new Map());
  // 연동 상품 관리 모달에 띄울 대상 (null이면 닫힘)
  const [manageTarget, setManageTarget] = useState<PathPickUnsyncTarget | null>(
    null
  );
  const [isBulkConfirmOpen, setIsBulkConfirmOpen] = useState<boolean>(false);
  // 모바일 필터 모달 — 좁은 폭에서 인라인 필터 줄 대신 이걸 띄운다.
  const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>(false);
  // 재생성 확인 모달 대상 — 단건은 1개, 일괄은 선택분 전체가 들어온다 (null이면 닫힘).
  const [retryTargets, setRetryTargets] = useState<
    PathPickRetryTarget[] | null
  >(null);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  // 모바일(md 미만)은 표 칼럼 합이 화면을 한참 넘어 가로 스크롤로만 읽히므로 상품 1건을 카드 1장으로 그린다.
  // 가로 스크롤은 뷰포트 폭 문제라 UA가 아니라 브레이크포인트로 가른다.
  const isNarrow = useBreakpointValue(
    { base: true, md: false },
    { ssr: false }
  );

  const { unsyncProducts, isPending: isUnsyncing } =
    usePathPickUnsyncProducts();
  const { retryRequests, isPending: isRetrying } = usePathPickRetryRequests();

  const { pathPickRequestDetail, isPending, isPlaceholderData } =
    useGetPathPickRequestDetail(requestId, {
      status__in: toStatusFilterParam(statusTab),
      query: query || undefined,
      pageNumber,
      pageSize,
    });
  // 요청 헤더도 상세 응답에서 읽는다 — 로딩 중에는 다운로드 요청 형태로 그리고,
  // 응답이 오면 연동 요청 컬럼으로 바뀐다 (그 사이 본문은 스피너다).
  // 다운로드 요청에는 연동 상태/연동 해제 개념이 없어 관련 UI를 모두 감춘다.
  const isApiSync = pathPickRequestDetail?.requestType === "SYNC";
  // 일괄 처리 영역에 남는 동작은 연동 해제뿐이다 — 연동이 실제로 걸린 요청,
  // 즉 API 연동이 완료/일부 완료로 끝난 요청에서만 의미가 있다.
  // 현재 페이지의 행이 아니라 요청 상태로 판정한다 — 페이지를 넘길 때 영역이 나타났다 사라지지 않게.
  const showBulkActions =
    isApiSync &&
    (pathPickRequestDetail?.status === "COMPLETED" ||
      pathPickRequestDetail?.status === "PARTIAL_COMPLETED");
  // 다운로드는 파일 요청에만 있다. 응답 전에는 isApiSync가 false로 시작하므로 상세가 실린 뒤에만
  // 그린다 — 그러지 않으면 연동 요청에도 비활성 KREAM 버튼이 한 번 스쳐 보인다.
  const showDownload = Boolean(pathPickRequestDetail) && !isApiSync;
  // 요청 1건은 채널 하나에 대한 신청이라, 연동 해제도 그 채널(format)에서만 한다.
  // store_names를 빼면 서버가 연결된 모든 마켓에서 내리므로 스토어를 못 정하면 해제를 막는다.
  const unsyncStoreName = isApiSync
    ? toPathPickStoreName(pathPickRequestDetail?.format)
    : null;
  // 일괄 영역이 없으면 고를 수 있는 행도 없어 선택 열은 비활성 체크박스만 남는다 — 열째 뺀다.
  const templateColumns = [
    showBulkActions ? SELECT_COLUMN : null,
    isApiSync ? TEMPLATE_COLUMNS : DOWNLOAD_TEMPLATE_COLUMNS,
  ]
    .filter(Boolean)
    .join(" ");
  const columnKeys = isApiSync ? SYNC_COLUMN_KEYS : DOWNLOAD_COLUMN_KEYS;

  // 요청 1건은 채널 하나에 대한 신청이라, 재생성 request_types도 요청 단위로 하나만 쓴다.
  // 다운로드 요청의 채널은 format으로 갈린다 (NAVER=스마트스토어 파일, 그 외=KREAM 파일).
  const requestKind: PathPickProductRequestKind = isApiSync
    ? "NAVER_SYNC"
    : pathPickRequestDetail?.format === "NAVER"
    ? "NAVER_EXCEL"
    : "KREAM_EXCEL";

  const products = pathPickRequestDetail?.items ?? [];
  const totalCount = pathPickRequestDetail?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // 열릴 때마다 필터·검색·선택을 초기화한다. 지금의 호출부는 모두 조건부 렌더라 매번 새로
  // 마운트되지만, isOpen만 토글하는 호출부가 생겨도 이전에 보던 검색어가 남지 않게 해 둔다.
  // 카드 배지로 열었을 때는 그 상품의 SKU(initialQuery)가 검색어로 적용된 상태에서 시작한다.
  //
  // deps에 initialQuery를 넣지 않는다 — 여는 시점의 초기화이지 prop 변경에 대한 초기화가 아니다.
  // 검색어를 URL에 싣는 호출부에서는 검색을 커밋할 때마다 initialQuery가 바뀌는데,
  // 그때 이 effect가 다시 돌면 상태 탭과 선택이 통째로 풀려버린다.
  // (초기 검색어는 search/query의 useState 초기값이 이미 잡아준다)
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const initial = initialQuery ?? "";
    setSearch(initial);
    setQuery(initial);
    setStatusTab("ALL");
    setPageNumber(1);
    setSelectedProducts(new Map());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, requestId]);

  // 연동 해제로 항목이 줄면 마지막 페이지가 사라질 수 있어 빈 페이지에 갇히지 않게 당겨준다.
  useEffect(() => {
    if (isPending || isPlaceholderData) {
      return;
    }
    if (pageNumber > totalPages) {
      setPageNumber(totalPages);
    }
  }, [isPending, isPlaceholderData, pageNumber, totalPages]);

  const handleChangePageSize = (size: number) => {
    setPageSize(size);
    setPageNumber(1);
  };

  // 검색은 서버가 처리한다 — 검색어가 바뀌면 totalCount 기준이 달라지므로 1페이지로 되돌린다.
  const handleSearch = () => {
    const nextQuery = search.trim();
    setQuery(nextQuery);
    setPageNumber(1);
    onQueryCommit?.(nextQuery);
  };

  // 상태 탭도 같은 규칙 — 결과 집합이 통째로 바뀌므로 1페이지로 되돌린다.
  const handleChangeStatusTab = (tab: PathPickRequestStatusTab) => {
    setStatusTab(tab);
    setPageNumber(1);
  };

  // 상태 축이 전체가 아니면 초기화 버튼을 노출한다 (검색어는 별도 축이라 건드리지 않는다).
  const isFilterActive = statusTab !== "ALL";

  // 모바일 필터 버튼의 배지 숫자 — 이 모달은 상태 한 축뿐이라 0 또는 1이다.
  const appliedFilterCount = isFilterActive ? 1 : 0;

  const handleResetFilters = () => {
    setStatusTab("ALL");
    setPageNumber(1);
  };

  // 빈 목록 안내 — 검색어가 가장 구체적인 원인이라 검색 → 상태 → 기본 순으로 고른다.
  const emptyState = query
    ? {
        titleKey: "path_pick:sync_download_history.no_search_results_title",
        descKey: "path_pick:sync_download_history.no_search_results_desc",
      }
    : isFilterActive
    ? {
        titleKey: "path_pick:sync_download_history.no_filter_results_title",
        descKey: "path_pick:sync_download_history.no_filter_results_desc",
      }
    : {
        titleKey: "path_pick:sync_download_history.no_products_title",
        descKey: null,
      };

  // 행별 상태 배지와 가능 동작을 한 번에 계산해 선택 판정과 렌더가 같은 값을 쓰게 한다.
  const rows = products.map((product) => {
    // 해제/삭제로 끝난 상품만 종료 상태 배지를 처리 상태보다 앞세운다.
    const statusMeta =
      getPathPickSyncStatusMeta(product.syncStatus, product.syncDeletedBy) ??
      getPathPickRequestItemStatusMeta(
        product.status,
        // 응답 전에는 요청 단위 상태를 알 수 없다 — 처리중으로 두는 게 가장 무해하다.
        pathPickRequestDetail?.status ?? "PROCESSING",
        isApiSync
      );
    // 연동 중인 상품만 해제할 수 있다 (해제/삭제된 상품과 실패 상품은 대상이 아니다).
    const canUnsync =
      unsyncStoreName !== null && isRequestDetailItemSyncActive(product);
    // 실패 판정을 한 번만 해 배지·비고·재생성이 같은 값을 보게 한다.
    const isFailed = isFailedStatusMeta(statusMeta);
    // 배지가 실패인 행만 재생성 대상 — 다시 신청해도 결과가 같은 사유는 제외한다.
    // 재생성 자체를 닫아 둔 동안(IS_REQUEST_RETRY_ENABLED)은 항상 false다.
    const canRetry =
      IS_REQUEST_RETRY_ENABLED &&
      isFailed &&
      isRetryableErrorType(product.errorType);

    return {
      product,
      statusMeta,
      isFailed,
      canUnsync,
      canRetry,
      isSelectable: canUnsync || canRetry,
    };
  });

  const selectableRows = rows.filter((row) => row.isSelectable);
  const isAllSelected =
    selectableRows.length > 0 &&
    selectableRows.every((row) => selectedProducts.has(row.product.id));
  const isIndeterminate =
    !isAllSelected &&
    selectableRows.some((row) => selectedProducts.has(row.product.id));

  const toSelectedItem = (row: (typeof rows)[number]): SelectedItem => ({
    id: row.product.productId,
    type: row.product.type,
    canUnsync: row.canUnsync,
    canRetry: row.canRetry,
  });

  // 선택분 중 각 기능이 실제로 적용되는 항목만 추린다 — 섞여 있으면 버튼은 둘 다 뜨지만 대상은 갈린다.
  // 키는 요청 아이템 id, 값의 id는 상품 id라 선택 해제와 요청 body가 서로 다른 id를 쓴다.
  const selectedEntries = Array.from(selectedProducts.entries());
  const unsyncEntries = selectedEntries.filter(
    ([, target]) => target.canUnsync
  );
  const retryEntries = selectedEntries.filter(([, target]) => target.canRetry);
  const bulkUnsyncProducts: PathPickMutationProduct[] = unsyncEntries.map(
    ([, target]) => ({ id: target.id, type: target.type })
  );
  const bulkRetryTargets: PathPickRetryTarget[] = retryEntries.map(
    ([, target]) => ({
      product: { id: target.id, type: target.type },
      kinds: [requestKind],
    })
  );

  const hasSelection = selectedProducts.size > 0;
  // 버튼 노출은 "현재 목록에 가능한 행이 있거나, 선택분에 대상이 있을 때" — 선택분까지 보는 게 핵심이다.
  // 목록만 보면 다른 페이지에서 고른 선택분을 처리할 버튼이 사라진다.
  const showRetry =
    bulkRetryTargets.length > 0 || selectableRows.some((row) => row.canRetry);
  // 연동 해제는 행이 아니라 요청 단위로 판정한다 — 마지막 연동 상품을 해제해도 버튼이 비활성으로 남아
  // 액션 줄 높이가 줄지 않는다(버튼이 빠지면 그만큼 아래 표가 늘어나 화면이 출렁인다).
  const showUnsync = unsyncStoreName !== null;

  const handleToggleAll = () => {
    setSelectedProducts((prev) => {
      // 하나라도 선택돼 있으면(전체/부분 모두) 누를 때 비우고, 아무것도 없을 때만 전체 선택한다.
      if (isAllSelected || isIndeterminate) {
        return new Map<number, SelectedItem>();
      }
      const next = new Map(prev);
      selectableRows.forEach((row) =>
        next.set(row.product.id, toSelectedItem(row))
      );
      return next;
    });
  };

  const handleToggleProduct = (row: (typeof rows)[number]) => {
    setSelectedProducts((prev) => {
      const next = new Map(prev);
      if (next.has(row.product.id)) {
        next.delete(row.product.id);
      } else {
        next.set(row.product.id, toSelectedItem(row));
      }
      return next;
    });
  };

  // 벌크 — 확인 모달에서 연동만 해제 / 상품 삭제를 고른다.
  // 해제한 항목만 선택에서 뺀다 — 재생성만 가능한 선택분은 그대로 남겨 이어서 처리할 수 있게 한다.
  const handleBulkUnsync = (deleteFromStore: boolean) => {
    if (!unsyncStoreName) {
      return;
    }
    const unsyncedItemIds = unsyncEntries.map(([itemId]) => itemId);
    unsyncProducts(bulkUnsyncProducts, deleteFromStore, {
      storeNames: [unsyncStoreName],
      onSuccess: () => {
        setSelectedProducts((prev) => {
          const next = new Map(prev);
          unsyncedItemIds.forEach((itemId) => next.delete(itemId));
          return next;
        });
        setIsBulkConfirmOpen(false);
      },
    });
  };

  // 단건 — 관리 모달에서 연동만 해제 / 상품 삭제를 고른다.
  const handleManageUnsync = (deleteFromStore: boolean) => {
    if (!manageTarget || !unsyncStoreName) {
      return;
    }
    const { id, type } = manageTarget;
    unsyncProducts([{ id, type }], deleteFromStore, {
      storeNames: [unsyncStoreName],
      onSuccess: () => {
        // 해제된 상품은 더 이상 선택 대상이 아니므로 선택 목록에서도 뺀다.
        // 유형까지 비교해야 숫자 id가 같은 다른 유형 상품의 선택이 함께 빠지지 않는다.
        const unsyncedKey = toPathPickProductKey(type, id);
        setSelectedProducts(
          (prev) =>
            new Map(
              Array.from(prev).filter(
                ([, product]) =>
                  toPathPickProductKey(product.type, product.id) !== unsyncedKey
              )
            )
        );
        setManageTarget(null);
      },
    });
  };

  // 재생성 — 단건/일괄 모두 같은 확인 모달을 거친다. 신청한 상품만 선택에서 뺀다.
  const handleRetry = () => {
    if (!retryTargets) {
      return;
    }
    // 유형까지 붙인 키로 비교한다 — 숫자 id만 보면 다른 유형의 같은 id 선택도 빠진다.
    const retriedProductKeys = new Set<PathPickProductKey>(
      retryTargets.map(({ product }) =>
        toPathPickProductKey(product.type, product.id)
      )
    );
    retryRequests(retryTargets, {
      onSuccess: () => {
        setSelectedProducts(
          (prev) =>
            new Map(
              Array.from(prev).filter(
                ([, target]) =>
                  !retriedProductKeys.has(
                    toPathPickProductKey(target.type, target.id)
                  )
              )
            )
        );
        setRetryTargets(null);
      },
    });
  };

  return (
    <>
      <CustomApprovalModal
        isOpen={isOpen}
        onClose={onClose}
        maxWidth={"90dvw"}
        // 화면이 좁으면 모달이 뷰포트를 넘지 않게 같이 내려온다 —
        // min-width가 max-width를 이기므로 여기서 함께 막아야 한다.
        minWidth={"min(1180px, 90dvw)"}
        // 상품 수와 요청 유형(연동/다운로드)에 따라 높이가 들쭉날쭉하지 않게 항상 80dvh로 고정한다.
        // maxHeight는 scrollBehavior="inside"가 넣는 상한(100vh - 120px)을 덮기 위한 것 — 없으면
        // 세로 600px 미만 화면에서 80dvh가 지켜지지 않는다.
        height={"85dvh"}
        maxHeight={"85dvh"}
        scrollBehavior={"inside"}
        isCentered={true}
      >
        <CustomApprovalModal.Header
          onClose={onClose}
          title={
            <Flex alignItems={"center"} gap={"12px"} flexWrap={"wrap"}>
              <Text textStyle={"body-md-bold"} color={"#444444"}>
                {t(
                  "path_pick:sync_download_history.synced_products_modal.title"
                )}
              </Text>
              <PathPickRequestStatusChip
                label={t(
                  "path_pick:sync_download_history.synced_products_modal.requested_at_chip",
                  {
                    datetime: pathPickRequestDetail
                      ? formatRequestDateTime(pathPickRequestDetail.requestedAt)
                      : "-",
                  }
                )}
              />
              <PathPickRequestStatusChip
                colorVariant={"blue"}
                label={t(
                  "path_pick:sync_download_history.synced_products_modal.chip_total",
                  { count: pathPickRequestDetail?.requestedItemCount ?? 0 }
                )}
              />
              {/* 완료/실패 건수는 표의 상태 배지·상태 필터와 겹쳐 두지 않는다. */}
              <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
                {isApiSync
                  ? t(
                      "path_pick:sync_download_history.synced_products_modal.latest_refresh",
                      {
                        datetime: pathPickRequestDetail?.completedAt
                          ? formatRequestDateTime(
                              pathPickRequestDetail.completedAt
                            )
                          : "-",
                      }
                    )
                  : t(
                      "path_pick:sync_download_history.synced_products_modal.snapshot_note"
                    )}
              </Text>
            </Flex>
          }
        />
        {/* 고정 높이 안에서 상품 목록만 늘고 줄게 — 스크롤 주인은 목록 하나뿐이다. */}
        <CustomApprovalModal.Body
          display={"flex"}
          flexDirection={"column"}
          overflow={"hidden"}
        >
          <Flex flexDirection={"column"} gap={"16px"} flex={1} minHeight={0}>
            {/* 검색과 상태 필터를 한 줄에 둔다 — 필터 축이 하나라 줄을 나눌 이유가 없다. */}
            <Flex
              gap={"12px"}
              alignItems={"center"}
              flexWrap={"wrap"}
              padding={"16px"}
              border={"0.5px solid #D4D4D1"}
              borderRadius={"8px"}
              flexShrink={0}
              justifyContent={"space-between"}
            >
              <Flex
                gap={"12px"}
                alignItems={"center"}
                flexWrap={"wrap"}
                /* 축소를 막으면 그룹이 max-content 폭을 유지해 안쪽 자식이 접힐 기회를
                   잃는다. minWidth 0까지 풀어야 검색창이 컨테이너를 따라 내려온다. */
                minWidth={0}
                flex={{ base: 1, md: "none" }}
              >
                {/* 모바일은 검색창과 필터 버튼만 한 줄에 둔다 — 검색은 키보드 검색(Enter)으로 제출하고,
                    건수는 아래 페이지네이션이 보여 준다. stretch로 필터 버튼이 검색창 실제 높이를 따라간다. */}
                <Flex
                  gap={"8px"}
                  alignItems={"stretch"}
                  width={{ base: "100%", md: "auto" }}
                >
                  {/* 검색창의 width는 문자열만 받아 반응형 폭은 바깥 Box가 맡는다. */}
                  <Box
                    flex={{ base: 1, md: "none" }}
                    minWidth={0}
                    width={{ base: "auto", md: "320px" }}
                  >
                    <SearchInput
                      value={search}
                      onChange={setSearch}
                      onKeyDown={handleSearch}
                      placeholder={t(
                        "path_pick:sync_download_history.synced_products_modal.search_placeholder"
                      )}
                      // 모바일 키보드의 엔터 키를 `검색`으로 보여준다 — 모바일엔 검색 버튼이 없다.
                      inputProps={{ enterKeyHint: "search" }}
                    />
                  </Box>
                  <PathPickRequestFilterModal.TriggerButton
                    appliedCount={appliedFilterCount}
                    onClick={() => setIsFilterModalOpen(true)}
                    height={"auto"}
                  />
                </Flex>
                {/* AppButton은 Tooltip으로 감싸여 있어 display 분기는 바깥 Flex에 건다. */}
                <Flex display={{ base: "none", md: "flex" }}>
                  <AppButton variant={"primary"} onClick={handleSearch}>
                    <Flex gap={"4px"} alignItems={"center"}>
                      <CustomIcon
                        src={"search"}
                        width={16}
                        height={16}
                        color={"#FFFFFF"}
                      />
                      <Text textStyle={"body-sm-bold"}>
                        {t(
                          "path_pick:sync_download_history.synced_products_modal.search"
                        )}
                      </Text>
                    </Flex>
                  </AppButton>
                </Flex>
                <Text
                  display={{ base: "none", md: "block" }}
                  textStyle={"body-sm-medium"}
                  color={"#444440"}
                >
                  {t(
                    "path_pick:sync_download_history.synced_products_modal.search_result",
                    { count: totalCount }
                  )}
                </Text>
                {/* 필터가 걸린 상태에서만 나타나는 텍스트 액션.
                    아래 인라인 필터 줄과 짝이라 데스크탑에만 남기고, 모바일에서는
                    필터 모달 안의 초기화가 대신한다. */}
                {isFilterActive && (
                  <Text
                    as={"button"}
                    type={"button"}
                    display={{ base: "none", md: "inline-block" }}
                    textStyle={"body-sm-medium"}
                    color={"#444440"}
                    _hover={{ color: "#1272EF" }}
                    onClick={handleResetFilters}
                  >
                    {t(
                      "path_pick:sync_download_history.synced_products_modal.reset_filter"
                    )}
                  </Text>
                )}
              </Flex>
              {/* 라벨과 탭 그룹은 쌍으로 묶는다 — 줄이 접혀도 라벨이 자기 탭에서 떨어지지 않는다.
                  좁은 폭에서는 탭이 여러 줄로 접혀 목록을 밀어내므로, 모바일에서는 이 줄을
                  숨기고 위의 필터 버튼 + 필터 모달로 같은 필터를 고른다. */}
              <Flex
                display={{ base: "none", md: "flex" }}
                gap={"8px"}
                alignItems={"center"}
                flexWrap={"wrap"}
              >
                <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
                  {t(
                    "path_pick:sync_download_history.synced_products_modal.filter_label_status"
                  )}
                </Text>
                <PathPickRequestFilterTabs
                  tabs={REQUEST_STATUS_TABS}
                  value={statusTab}
                  labelKey={REQUEST_STATUS_TAB_LABEL_KEY}
                  onChange={handleChangeStatusTab}
                />
              </Flex>
            </Flex>

            {/* 표 위 액션 줄 — 연동 요청은 일괄 처리(선택·연동 해제), 파일 요청은 다운로드가 온다.
                둘 다 없으면 줄 자체를 만들지 않아 목록에 높이를 넘긴다. */}
            {(showBulkActions || showDownload) && (
              <Flex
                justifyContent={"space-between"}
                alignItems={"center"}
                gap={"16px"}
                flexWrap={"wrap"}
                // 목록에 높이를 돌려주려고 테두리·배경과 상하 패딩은 두지 않는다 —
                // 선택 여부는 칩 색(blue/gray)이 그대로 보여준다.
                padding={"0 10px"}
                flexShrink={0}
              >
                {showBulkActions && (
                  <>
                    <PathPickRequestStatusChip
                      colorVariant={hasSelection ? "blue" : "gray"}
                      label={t(
                        "path_pick:sync_download_history.synced_products_modal.selected_chip",
                        { count: selectedProducts.size }
                      )}
                    />
                    <Flex gap={"12px"} alignItems={"center"} flexWrap={"wrap"}>
                      {showRetry && (
                        <AppButton
                          variant={"primary"}
                          isDisabled={bulkRetryTargets.length === 0}
                          onClick={() => setRetryTargets(bulkRetryTargets)}
                          {...lockedProps}
                        >
                          <Flex gap={"4px"} alignItems={"center"}>
                            <CustomIcon
                              src={"arrow-rotate"}
                              width={16}
                              height={16}
                              color={
                                bulkRetryTargets.length === 0
                                  ? "#D4D4D1"
                                  : "#FFFFFF"
                              }
                            />
                            {/* 대상이 없을 때 "선택 0개 재생성"이 되지 않게 카운트 없는 라벨을 쓴다. */}
                            <Text textStyle={"body-sm-bold"}>
                              {bulkRetryTargets.length === 0
                                ? t(
                                    "path_pick:sync_download_history.synced_products_modal.retry"
                                  )
                                : t(
                                    "path_pick:sync_download_history.synced_products_modal.bulk_retry",
                                    { count: bulkRetryTargets.length }
                                  )}
                            </Text>
                          </Flex>
                        </AppButton>
                      )}
                      {showUnsync && (
                        <AppButton
                          variant={"white"}
                          isDisabled={bulkUnsyncProducts.length === 0}
                          onClick={() => setIsBulkConfirmOpen(true)}
                          {...lockedProps}
                        >
                          <Flex gap={"4px"} alignItems={"center"}>
                            <CustomIcon
                              src={"x-circle"}
                              width={16}
                              height={16}
                              color={
                                bulkUnsyncProducts.length === 0
                                  ? "#8F8F8C"
                                  : "#EE7D7A"
                              }
                            />
                            <Text
                              textStyle={"body-sm-bold"}
                              color={
                                bulkUnsyncProducts.length === 0
                                  ? "#8F8F8C"
                                  : "#EE7D7A"
                              }
                            >
                              {bulkUnsyncProducts.length === 0
                                ? t(
                                    "path_pick:sync_download_history.synced_products_modal.unsync"
                                  )
                                : t(
                                    "path_pick:sync_download_history.synced_products_modal.bulk_unsync",
                                    { count: bulkUnsyncProducts.length }
                                  )}
                            </Text>
                          </Flex>
                        </AppButton>
                      )}
                      {/* Text 버튼에는 isDisabled가 없어 색·커서·핸들러로 비활성을 표현한다. */}
                      <Text
                        as={"button"}
                        type={"button"}
                        textStyle={"body-sm-medium"}
                        color={hasSelection ? "#444440" : "#D4D4D1"}
                        cursor={hasSelection ? "pointer" : "not-allowed"}
                        _hover={hasSelection ? { color: "#1272EF" } : undefined}
                        onClick={
                          hasSelection
                            ? () => setSelectedProducts(new Map())
                            : undefined
                        }
                      >
                        {t(
                          "path_pick:sync_download_history.synced_products_modal.clear_selection"
                        )}
                      </Text>
                    </Flex>
                  </>
                )}
                {/* 파일 요청은 이 줄에 다운로드만 온다 — 왼쪽이 비어도 오른쪽 끝에 붙게 auto 마진을 준다.
                    등록 신청 내역 목록의 다운로드 칼럼과 같은 컴포넌트라 문구·스타일·동작이 같다. */}
                {showDownload && (
                  <Flex marginLeft={"auto"}>
                    <PathPickRequestFileDownloadButton
                      fileUrl={pathPickRequestDetail?.fileUrl ?? null}
                      format={pathPickRequestDetail?.format ?? null}
                    />
                  </Flex>
                )}
              </Flex>
            )}

            {/* 모달의 남는 높이를 전부 흡수하는 유일한 가변 영역 */}
            <Flex
              flexDirection={"column"}
              border={"0.5px solid #D4D4D1"}
              borderRadius={"8px"}
              /* 표가 모달보다 넓으면 가로로 스크롤한다(카드는 폭에 맞춰 접혀 필요 없다).
                 세로 스크롤 주인은 아래 목록이라 여기서는 세로를 잠가 스크롤 축이 겹치지 않게 한다. */
              overflowX={isNarrow ? "hidden" : "auto"}
              overflowY={"hidden"}
              flex={1}
              minHeight={0}
            >
              {/* 헤더와 행이 같은 폭을 공유해야 가로 스크롤 중에도 열이 어긋나지 않는다. */}
              <Flex
                flexDirection={"column"}
                flex={1}
                minHeight={0}
                minWidth={isNarrow ? 0 : "fit-content"}
              >
                {/* 카드는 칸마다 라벨을 붙여 그리므로 칼럼 헤더 대신 전체 선택 줄만 둔다. */}
                {isNarrow ? (
                  showBulkActions && (
                    <Flex
                      padding={"8px 12px"}
                      background={"#F7F9FA"}
                      borderBottom={"0.5px solid #D4D4D1"}
                      flexShrink={0}
                    >
                      <AppCheckBox
                        size={"small"}
                        name={t("common:select_all")}
                        isChecked={isAllSelected}
                        isIndeterminate={isIndeterminate}
                        isDisabled={selectableRows.length === 0}
                        onChange={handleToggleAll}
                      />
                    </Flex>
                  )
                ) : (
                  <Grid
                    templateColumns={templateColumns}
                    gap={"16px"}
                    padding={"12px 16px"}
                    background={"#F7F9FA"}
                    borderBottom={"0.5px solid #D4D4D1"}
                    flexShrink={0}
                  >
                    {showBulkActions && (
                      <Flex>
                        <AppCheckBox
                          size={"small"}
                          isChecked={isAllSelected}
                          isIndeterminate={isIndeterminate}
                          isDisabled={selectableRows.length === 0}
                          onChange={handleToggleAll}
                        />
                      </Flex>
                    )}
                    {columnKeys.map((key) => (
                      <Text
                        key={key}
                        textStyle={"body-sm-medium"}
                        color={"#8F8F8C"}
                        // 재고는 숫자 열이라 값과 함께 가운데로 맞춘다.
                        textAlign={
                          key === "column_stock" ? "center" : undefined
                        }
                      >
                        {t(
                          `path_pick:sync_download_history.synced_products_modal.${key}`
                        )}
                      </Text>
                    ))}
                  </Grid>
                )}
                <Box position={"relative"} flex={1} minHeight={0}>
                  <Flex
                    flexDirection={"column"}
                    height={"100%"}
                    overflowY={"auto"}
                  >
                    {isPending && (
                      <Flex padding={"32px 0"}>
                        <CenteredSpinner />
                      </Flex>
                    )}
                    {/* 검색 결과가 0건인 경우가 흔하다 — 고정 높이 표가 설명 없이 비지 않게 안내를 둔다. */}
                    {!isPending && products.length === 0 && (
                      <Flex
                        flex={1}
                        flexDirection={"column"}
                        justifyContent={"center"}
                        padding={"16px"}
                      >
                        <CustomNoResults
                          title={t(emptyState.titleKey)}
                          description={
                            emptyState.descKey
                              ? t(emptyState.descKey)
                              : undefined
                          }
                        />
                      </Flex>
                    )}
                    {!isPending &&
                      rows.map((row, index) => {
                        const { product, statusMeta } = row;
                        // 옵션이 없으면 재고를 알 수 없는 상태라 합계 대신 null로 둔다.
                        const totalQuantity = product.options?.length
                          ? getTotalOptionQuantity(product.options)
                          : null;
                        const isSoldOut = totalQuantity === 0;
                        // 현재 재고 — 옵션(사이즈)별 재고의 합. 옵션이 없으면 알 수 없는 값이라 "-"로 둔다.
                        // 합계가 0이면 숫자 대신 품절 문구로 상태를 바로 읽히게 한다.
                        const stockLabel =
                          totalQuantity === null
                            ? "-"
                            : isSoldOut
                            ? t(
                                "path_pick:sync_download_history.synced_products_modal.stock_sold_out"
                              )
                            : totalQuantity.toLocaleString();
                        const stockColor = isSoldOut ? "#EE7D7A" : "#1D1D1B";

                        // 서버 오류 코드는 사용자가 스스로 조치할 수 없어 문의 안내로 묶고, 사진 없음·마켓에 없는
                        // 브랜드처럼 이유를 알면 되는 사유만 따로 보여 준다.
                        // 마켓 하루 등록 한도로 요청이 대기 중이면, 아직 결과가 없는 상품에 그 이유를 알려 준다.
                        const note = row.isFailed
                          ? t(
                              `path_pick:sync_download_history.synced_products_modal.${getFailureNoteKey(
                                row.product.errorType
                              )}`
                            )
                          : pathPickRequestDetail?.waitingReason ===
                              "MARKET_DAILY_LIMIT" &&
                            row.product.status === null
                          ? t(
                              "path_pick:sync_download_history.synced_products_modal.note_market_daily_limit_waiting"
                            )
                          : null;
                        const noteColor = row.isFailed ? "#EE7D7A" : "#8F8F8C";

                        const checkbox = showBulkActions && (
                          <AppCheckBox
                            size={"small"}
                            isChecked={selectedProducts.has(product.id)}
                            isDisabled={!row.isSelectable}
                            onChange={() => handleToggleProduct(row)}
                          />
                        );

                        const productInfo = (
                          <Flex gap={"12px"} alignItems={"center"} minWidth={0}>
                            <Image
                              src={product.imageUrl ?? ""}
                              fallbackSrc={"/icons-v1_1/null.png"}
                              boxSize={"48px"}
                              objectFit={"contain"}
                              borderRadius={"8px"}
                              border={"0.5px solid #EFEFED"}
                              background={"#FFFFFF"}
                              alt={product.name || product.modelNumber}
                            />
                            <Flex
                              flexDirection={"column"}
                              gap={"2px"}
                              minWidth={0}
                            >
                              <Text
                                textStyle={"caption-medium"}
                                color={"#8F8F8C"}
                                noOfLines={1}
                                wordBreak={"break-all"}
                              >
                                {product.brandName}
                              </Text>
                              <PathPickCopyableSkuText sku={product.sku} />
                              {/* 상품명은 미기입인 경우가 있어 모델번호로 폴백한다. */}
                              <Text
                                textStyle={"body-md-bold"}
                                color={"#1D1D1B"}
                                noOfLines={1}
                                wordBreak={"break-all"}
                              >
                                {product.name || product.modelNumber}
                              </Text>
                            </Flex>
                          </Flex>
                        );

                        const statusBadge = (
                          <PathPickBadge
                            label={t(
                              `path_pick:sync_download_history.${statusMeta.labelKey}`
                            )}
                            colorVariant={statusMeta.colorVariant}
                            leadingIcon={statusMeta.leadingIcon}
                          />
                        );

                        // 관리 — 연동 중이면 연동 해제, 재시도 가능한 실패면 재생성. 걸 동작이 없으면 null.
                        const manageButton = row.canUnsync ? (
                          <AppButton
                            variant={"white"}
                            onClick={() =>
                              setManageTarget({
                                id: product.productId,
                                type: product.type,
                                brandName: product.brandName,
                                name: product.name || product.modelNumber,
                                sku: product.sku,
                              })
                            }
                            {...lockedProps}
                          >
                            <Flex gap={"4px"} alignItems={"center"}>
                              <CustomIcon
                                src={"x-circle"}
                                width={16}
                                height={16}
                                color={"#EE7D7A"}
                              />
                              <Text
                                textStyle={"body-sm-bold"}
                                color={"#EE7D7A"}
                              >
                                {t(
                                  "path_pick:sync_download_history.synced_products_modal.unsync"
                                )}
                              </Text>
                            </Flex>
                          </AppButton>
                        ) : row.canRetry ? (
                          <AppButton
                            variant={"primary"}
                            onClick={() =>
                              setRetryTargets([
                                {
                                  product: {
                                    id: product.productId,
                                    type: product.type,
                                  },
                                  kinds: [requestKind],
                                },
                              ])
                            }
                            {...lockedProps}
                          >
                            <Flex gap={"4px"} alignItems={"center"}>
                              <CustomIcon
                                src={"arrow-rotate"}
                                width={16}
                                height={16}
                                color={"#FFFFFF"}
                              />
                              <Text textStyle={"body-sm-bold"}>
                                {t(
                                  "path_pick:sync_download_history.synced_products_modal.retry"
                                )}
                              </Text>
                            </Flex>
                          </AppButton>
                        ) : null;

                        if (isNarrow) {
                          return (
                            <Flex
                              key={product.id}
                              flexDirection={"column"}
                              gap={"10px"}
                              padding={"12px"}
                              borderTop={
                                index > 0 ? "0.5px solid #D4D4D1" : undefined
                              }
                            >
                              <Flex
                                gap={"8px"}
                                alignItems={"center"}
                                minWidth={0}
                              >
                                {checkbox}
                                {productInfo}
                              </Flex>
                              {/* 표의 칼럼 라벨을 그대로 붙여 라벨 / 값 두 칸으로 세운다. */}
                              <Grid
                                templateColumns={"auto 1fr"}
                                columnGap={"12px"}
                                rowGap={"4px"}
                                alignItems={"baseline"}
                              >
                                <CardFieldLabel>
                                  {t(
                                    "path_pick:sync_download_history.synced_products_modal.column_stock"
                                  )}
                                </CardFieldLabel>
                                <Text
                                  textStyle={"body-sm-bold"}
                                  color={stockColor}
                                >
                                  {stockLabel}
                                </Text>
                                <CardFieldLabel>
                                  {t(
                                    `path_pick:sync_download_history.synced_products_modal.${
                                      isApiSync
                                        ? "column_synced_at"
                                        : "column_requested_at"
                                    }`
                                  )}
                                </CardFieldLabel>
                                <Text
                                  textStyle={"body-sm-medium"}
                                  color={"#444440"}
                                >
                                  {formatRequestDateTime(product.createdAt)}
                                </Text>
                                {isApiSync && (
                                  <>
                                    <CardFieldLabel>
                                      {t(
                                        "path_pick:sync_download_history.synced_products_modal.column_refreshed_at"
                                      )}
                                    </CardFieldLabel>
                                    <Text
                                      textStyle={"body-sm-medium"}
                                      color={"#444440"}
                                    >
                                      {formatRequestDateTime(product.updatedAt)}
                                    </Text>
                                  </>
                                )}
                                {note && (
                                  <>
                                    <CardFieldLabel>
                                      {t(
                                        "path_pick:sync_download_history.synced_products_modal.column_note"
                                      )}
                                    </CardFieldLabel>
                                    <Text
                                      textStyle={"body-sm-medium"}
                                      color={noteColor}
                                      wordBreak={"break-word"}
                                    >
                                      {note}
                                    </Text>
                                  </>
                                )}
                              </Grid>
                              {/* 관리 동작이 없는 카드는 버튼 자리를 비운다 — 표처럼 칸을 채울 "-"가 필요 없다. */}
                              <Flex
                                justifyContent={"space-between"}
                                alignItems={"center"}
                                gap={"8px"}
                              >
                                {statusBadge}
                                {manageButton}
                              </Flex>
                            </Flex>
                          );
                        }

                        return (
                          <Grid
                            key={product.id}
                            templateColumns={templateColumns}
                            gap={"16px"}
                            alignItems={"center"}
                            padding={"16px"}
                            borderTop={
                              index > 0 ? "0.5px solid #EFEFED" : undefined
                            }
                          >
                            {checkbox}

                            {productInfo}

                            <Text
                              textStyle={"body-md-medium"}
                              color={stockColor}
                              noOfLines={1}
                              textAlign={"center"}
                            >
                              {stockLabel}
                            </Text>

                            {/* 연동일 / 다운로드는 신청일 — 둘 다 상품 행 생성 시각을 쓴다. */}
                            <Text
                              textStyle={"body-sm-medium"}
                              color={"#444440"}
                            >
                              {formatRequestDateTime(product.createdAt)}
                            </Text>

                            {isApiSync && (
                              <Text
                                textStyle={"body-sm-medium"}
                                color={"#444440"}
                              >
                                {formatRequestDateTime(product.updatedAt)}
                              </Text>
                            )}

                            <Flex>{statusBadge}</Flex>

                            {/* 좁은 칸에서 잘리지 않게 줄바꿈으로 흘린다. */}
                            <Text
                              textStyle={"body-sm-medium"}
                              color={noteColor}
                              wordBreak={"break-word"}
                            >
                              {note ?? "-"}
                            </Text>

                            {/* 걸 수 있는 액션이 없는 행(해제·삭제·재시도 불가 실패·미처리)은
                                비고 칼럼과 같은 "-"를 둬서 관리 칼럼이 비지 않게 한다. */}
                            {manageButton ? (
                              <Flex>{manageButton}</Flex>
                            ) : (
                              <Text
                                textStyle={"body-sm-medium"}
                                color={"#8F8F8C"}
                              >
                                {"-"}
                              </Text>
                            )}
                          </Grid>
                        );
                      })}
                  </Flex>
                  {/* 페이지 전환 중에는 이전 목록 위에 오버레이 스피너만 덮는다 */}
                  {isPlaceholderData && (
                    <Box
                      position={"absolute"}
                      inset={0}
                      background={"rgba(255, 255, 255, 0.6)"}
                    >
                      <CenteredSpinner />
                    </Box>
                  )}
                </Box>
              </Flex>
            </Flex>

            {totalCount > 0 && (
              <Box flexShrink={0}>
                <PathPickPagination
                  total={totalCount}
                  pageNumber={pageNumber}
                  pageSize={pageSize}
                  onChangePage={setPageNumber}
                  onChangePageSize={handleChangePageSize}
                />
              </Box>
            )}
          </Flex>
        </CustomApprovalModal.Body>
      </CustomApprovalModal>

      {isFilterModalOpen && (
        <PathPickRequestFilterModal
          isOpen
          onClose={() => setIsFilterModalOpen(false)}
          isFilterActive={isFilterActive}
          onReset={handleResetFilters}
          resetLabel={t(
            "path_pick:sync_download_history.synced_products_modal.reset_filter"
          )}
        >
          {/* 인라인 줄과 완전히 같은 인자를 넘긴다 — 상태와 핸들러를 공유하므로
              어느 쪽에서 고르든 결과가 같다. */}
          <PathPickRequestFilterModal.Group
            label={t(
              "path_pick:sync_download_history.synced_products_modal.filter_label_status"
            )}
          >
            <PathPickRequestFilterTabs
              tabs={REQUEST_STATUS_TABS}
              value={statusTab}
              labelKey={REQUEST_STATUS_TAB_LABEL_KEY}
              onChange={handleChangeStatusTab}
            />
          </PathPickRequestFilterModal.Group>
        </PathPickRequestFilterModal>
      )}

      {isBulkConfirmOpen && (
        <PathPickUnsyncConfirmModal
          isOpen
          onClose={() => setIsBulkConfirmOpen(false)}
          count={bulkUnsyncProducts.length}
          isLoading={isUnsyncing}
          onUnsyncOnly={() => handleBulkUnsync(false)}
          onDelete={() => handleBulkUnsync(true)}
        />
      )}

      {manageTarget && (
        <PathPickManageProductModal
          isOpen
          onClose={() => setManageTarget(null)}
          target={manageTarget}
          isLoading={isUnsyncing}
          onUnsyncOnly={() => handleManageUnsync(false)}
          onDelete={() => handleManageUnsync(true)}
        />
      )}

      {retryTargets && (
        <PathPickRetryConfirmModal
          isOpen
          onClose={() => setRetryTargets(null)}
          count={retryTargets.length}
          isLoading={isRetrying}
          onConfirm={handleRetry}
        />
      )}
    </>
  );
}

// 모바일 카드의 라벨 / 값 줄에서 라벨 칸 — 표 헤더와 같은 글자색을 쓴다.
const CardFieldLabel = ({ children }: { children: React.ReactNode }) => (
  <Text textStyle={"caption-medium"} color={"#8F8F8C"} whiteSpace={"nowrap"}>
    {children}
  </Text>
);

export default PathPickRequestProductsModal;
